import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';

import {
  EMPTY_COMBO,
  EMPTY_MODIFIER,
  EMPTY_MODIFIER_GROUP,
} from '../../features/restaurant/menu/configuration/menuConfigurationConstants';
import {
  ComboForm,
  MenuConfigurationTab,
  ModifierForm,
  ModifierGroupForm,
} from '../../features/restaurant/menu/configuration/types';
import {
  modifierFormToRequest,
  modifierInventoryIsValid,
  normalizeMenuConfigurationModifierGroups,
} from '../../features/restaurant/menu/configuration/menuConfigurationUtils';
import BranchService from '../../services/branch/BranchService';
import { useAuthUser } from '../auth/useAuthUser';
import RestaurantCatalogExtensionService from '../../services/restaurant/RestaurantCatalogExtensionService';
import RestaurantMenuItemService from '../../services/restaurant/RestaurantMenuItemService';
import RestaurantMenuCategoryService from '../../services/restaurant/RestaurantMenuCategoryService';
import RestaurantRawMaterialService from '../../services/restaurant/RestaurantRawMaterialService';
import { IBranchDropdown } from '../../types/branches/branchInterfaces';
import { PageMetadata } from '../../types/restaurant/PagedResponse';
import {
  RestaurantComboDTO,
  RestaurantComboFilters,
  RestaurantComboRequest,
  RestaurantModifierFilters,
  RestaurantModifierGroupFilters,
  RestaurantModifierGroupDTO,
  RestaurantModifierGroupRequest,
} from '../../types/restaurant/RestaurantCatalogExtensionTypes';
import { RestaurantMenuCategoryDTO, RestaurantMenuItemDTO } from '../../types/restaurant/RestaurantMenuTypes';
import {
  RawMaterialUnitOption,
  RestaurantRawMaterialDTO,
} from '../../types/restaurant/RestaurantRawMaterialTypes';
import useAlerts from '../useAlerts';
import useApiErrorHandler from '../useApiErrorHandler';
import useDebouncedValue from '../useDebouncedValue';

const emptyPage = (): PageMetadata => ({
  number: 0,
  size: 20,
  totalElements: 0,
  totalPages: 0,
});

const useRestaurantMenuConfiguration = () => {
  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();
  const handleRef = useRef(handle);
  handleRef.current = handle;
  const { hasRole } = useAuthUser();
  const canPickBranch =
    hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');
  const [branches, setBranches] = useState<IBranchDropdown[]>([]);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [branchError, setBranchError] = useState('');
  const [groups, setGroups] = useState<RestaurantModifierGroupDTO[]>([]);
  const [rawMaterials, setRawMaterials] = useState<RestaurantRawMaterialDTO[]>(
    []
  );
  const [units, setUnits] = useState<RawMaterialUnitOption[]>([]);
  const [combos, setCombos] = useState<RestaurantComboDTO[]>([]);
  const [menuItems, setMenuItems] = useState<RestaurantMenuItemDTO[]>([]);
  const [menuItemCache, setMenuItemCache] = useState<Record<number, RestaurantMenuItemDTO>>({});
  const [menuCategories, setMenuCategories] = useState<RestaurantMenuCategoryDTO[]>([]);
  const [tab, setTab] = useState<MenuConfigurationTab>('MODIFIERS');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedDish, setSelectedDish] =
    useState<RestaurantMenuItemDTO | null>(null);
  const [assignedGroups, setAssignedGroups] = useState<number[]>([]);
  const [groupForm, setGroupForm] =
    useState<ModifierGroupForm>(EMPTY_MODIFIER_GROUP);
  const [modifierForm, setModifierForm] =
    useState<ModifierForm>(EMPTY_MODIFIER);
  const [comboForm, setComboForm] = useState<ComboForm>(EMPTY_COMBO);
  const [modifierGroupFilters, setModifierGroupFilters] = useState<RestaurantModifierGroupFilters>({ page: 0, size: 20 });
  const [modifierFilters, setModifierFilters] = useState<RestaurantModifierFilters>({ page: 0, size: 20 });
  const [comboFilters, setComboFilters] = useState<RestaurantComboFilters>({ page: 0, size: 20 });
  const [menuSearch, setMenuSearch] = useState('');
  const [menuCategoryId, setMenuCategoryId] = useState(0);
  const [menuPage, setMenuPage] = useState(0);
  const [menuSize, setMenuSize] = useState(20);
  const [groupPage, setGroupPage] = useState<PageMetadata>(emptyPage());
  const [modifierPage, setModifierPage] = useState<PageMetadata>(emptyPage());
  const [comboPage, setComboPage] = useState<PageMetadata>(emptyPage());
  const [menuItemPage, setMenuItemPage] = useState<PageMetadata>(emptyPage());
  const debouncedMenuSearch = useDebouncedValue(menuSearch, 350);

  useEffect(() => {
    setBranchError('');
    const request = canPickBranch
      ? BranchService.fetchDropdown()
      : BranchService.fetchMyBranch().then((mine) => [mine]);

    request
      .then((items) => {
        setBranches(items);
        if (items.length) setBranchId(items[0].id);
      })
      .catch((error: any) => {
        setBranchError(
          error?.response?.data?.message ||
            'No fue posible cargar las sucursales.'
        );
      })
      .finally(() => setLoadingBranches(false));
  }, [canPickBranch]);

  const load = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);

    try {
      const [
        groupResponse,
        modifierResponse,
        comboResponse,
        menuResponse,
        rawMaterialResponse,
        menuCategoryResponse,
        unitOptions,
      ] = await Promise.all([
        RestaurantCatalogExtensionService.listModifierGroups(branchId, modifierGroupFilters),
        RestaurantCatalogExtensionService.listModifiers(branchId, modifierFilters),
        RestaurantCatalogExtensionService.listCombos(branchId, comboFilters),
        RestaurantMenuItemService.listFiltered(branchId, {
          page: menuPage,
          size: menuSize,
          sort: ['displayOrder,asc', 'id,asc'],
          filters: {
            search: debouncedMenuSearch.trim() || null,
            categoryId: menuCategoryId || null,
            available: true,
            featured: null,
            active: true,
          },
        }),
        RestaurantRawMaterialService.listFiltered(branchId, {
          page: 0,
          size: 100,
          sort: ['name,asc'],
          filters: {
            search: null,
            categoryId: null,
            unitId: null,
            supplierId: null,
            active: true,
          },
        }),
        RestaurantMenuCategoryService.listFiltered(branchId, {
          page: 0,
          size: 50,
          search: null,
          active: true,
        }),
        RestaurantRawMaterialService.units(),
      ]);

      setGroups(
        normalizeMenuConfigurationModifierGroups(groupResponse.content).map((group) => ({
          ...group,
          modifiers: modifierResponse.content.filter((modifier) => modifier.groupId === group.id),
        }))
      );
      setCombos(comboResponse.content);
      setMenuItems(menuResponse.content);
      setMenuItemCache((current) => {
        const next = { ...current };
        menuResponse.content.forEach((item) => { next[item.id] = item; });
        return next;
      });
      setMenuCategories(menuCategoryResponse.content);
      setRawMaterials(rawMaterialResponse.content);
      setUnits(unitOptions ?? []);
      setGroupPage(groupResponse.page);
      setModifierPage(modifierResponse.page);
      setComboPage(comboResponse.page);
      setMenuItemPage(menuResponse.page);
    } catch (error) {
      handleRef.current(error);
    } finally {
      setLoading(false);
    }
  }, [branchId, comboFilters, debouncedMenuSearch, menuCategoryId, menuPage, menuSize, modifierFilters, modifierGroupFilters]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setSelectedDish(null);
    setAssignedGroups([]);
    setGroupForm(EMPTY_MODIFIER_GROUP);
    setModifierForm(EMPTY_MODIFIER);
    setComboForm(EMPTY_COMBO);
    setModifierGroupFilters({ page: 0, size: 20 });
    setModifierFilters({ page: 0, size: 20 });
    setComboFilters({ page: 0, size: 20 });
    setMenuSearch('');
    setMenuCategoryId(0);
    setMenuPage(0);
    setMenuItemCache({});
  }, [branchId]);

  const saveGroup = async (event: FormEvent) => {
    event.preventDefault();
    if (
      !branchId ||
      groupForm.displayOrder < 0 ||
      groupForm.minSelections < 0 ||
      groupForm.maxSelections < 0 ||
      groupForm.maxSelections < groupForm.minSelections ||
      (groupForm.required && groupForm.minSelections < 1)
    ) {
      return;
    }

    setSaving(true);
    try {
      const body: RestaurantModifierGroupRequest = {
        name: groupForm.name.trim(),
        description: groupForm.description || null,
        minSelections: groupForm.minSelections,
        maxSelections: groupForm.maxSelections,
        required: groupForm.required,
        displayOrder: groupForm.displayOrder,
        active: groupForm.active,
      };

      const response = groupForm.id
        ? await RestaurantCatalogExtensionService.updateModifierGroup(
            branchId,
            groupForm.id,
            body
          )
        : await RestaurantCatalogExtensionService.createModifierGroup(
            branchId,
            body
          );

      setGroupForm(EMPTY_MODIFIER_GROUP);
      await load();
      show(
        response.type || 'success',
        response.title || 'Grupo guardado',
        response.message || 'El grupo de modificadores se guardó correctamente.'
      );
    } catch (error) {
      handle(error);
    } finally {
      setSaving(false);
    }
  };

  const saveModifier = async (event: FormEvent) => {
    event.preventDefault();
    if (
      !branchId ||
      !modifierForm.groupId ||
      modifierForm.displayOrder < 0 ||
      !Number.isFinite(modifierForm.priceAdjustment) ||
      !modifierInventoryIsValid(modifierForm)
    ) {
      return;
    }

    setSaving(true);
    try {
      const body = modifierFormToRequest(modifierForm);
      const response = modifierForm.id
        ? await RestaurantCatalogExtensionService.updateModifier(
            branchId,
            modifierForm.id,
            body
          )
        : await RestaurantCatalogExtensionService.createModifier(
            branchId,
            body
          );

      setModifierForm(EMPTY_MODIFIER);
      await load();
      show(
        response.type || 'success',
        response.title || 'Modificador guardado',
        response.message || 'El modificador se guardó correctamente.'
      );
    } catch (error) {
      handle(error);
    } finally {
      setSaving(false);
    }
  };

  const saveCombo = async (event: FormEvent) => {
    event.preventDefault();
    const menuItemIds = comboForm.items.map((item) => item.menuItemId);
    if (
      !branchId ||
      !comboForm.items.length ||
      comboForm.displayOrder < 0 ||
      comboForm.price < 0 ||
      new Set(menuItemIds).size !== menuItemIds.length ||
      comboForm.items.some(
        (item) =>
          item.displayOrder < 0 ||
          !Number.isInteger(item.quantity) ||
          item.quantity < 1
      )
    ) {
      return;
    }

    setSaving(true);
    try {
      const body: RestaurantComboRequest = {
        code: comboForm.code.trim().toUpperCase(),
        name: comboForm.name.trim(),
        description: comboForm.description || null,
        price: comboForm.price,
        available: comboForm.available,
        displayOrder: comboForm.displayOrder,
        active: comboForm.active,
        items: comboForm.items.map((item, index) => ({
          menuItemId: item.menuItemId,
          quantity: item.quantity,
          displayOrder: index,
        })),
      };

      const response = comboForm.id
        ? await RestaurantCatalogExtensionService.updateCombo(
            branchId,
            comboForm.id,
            body
          )
        : await RestaurantCatalogExtensionService.createCombo(branchId, body);

      setComboForm(EMPTY_COMBO);
      await load();
      show(
        response.type || 'success',
        response.title || 'Combo guardado',
        response.message || 'El combo se guardó correctamente.'
      );
    } catch (error) {
      handle(error);
    } finally {
      setSaving(false);
    }
  };

  const selectDish = async (dish: RestaurantMenuItemDTO) => {
    setSelectedDish(dish);
    setLoading(true);

    try {
      const response =
        await RestaurantCatalogExtensionService.menuItemModifierGroups(dish.id);
      setAssignedGroups(
        normalizeMenuConfigurationModifierGroups(response.data).map(
          (group) => group.id
        )
      );
    } catch (error) {
      handle(error);
    } finally {
      setLoading(false);
    }
  };

  const saveDishGroups = async () => {
    if (!selectedDish) return;

    setSaving(true);
    try {
      const response =
        await RestaurantCatalogExtensionService.assignMenuItemModifierGroups(
          selectedDish.id,
          assignedGroups
        );
      setAssignedGroups(
        normalizeMenuConfigurationModifierGroups(response.data).map(
          (group) => group.id
        )
      );
      show(
        response.type || 'success',
        response.title || 'Asignación guardada',
        response.message ||
          'Los grupos del plato se actualizaron correctamente.'
      );
    } catch (error) {
      handle(error);
    } finally {
      setSaving(false);
    }
  };

  return {
    branches,
    branchId,
    setBranchId,
    loadingBranches,
    branchError,
    canPickBranch,
    groups,
    rawMaterials,
    units,
    combos,
    menuItems,
    menuItemCache,
    menuCategories,
    tab,
    setTab,
    loading,
    saving,
    selectedDish,
    assignedGroups,
    setAssignedGroups,
    groupForm,
    setGroupForm,
    modifierForm,
    setModifierForm,
    comboForm,
    setComboForm,
    modifierGroupFilters,
    setModifierGroupFilters,
    modifierFilters,
    setModifierFilters,
    comboFilters,
    setComboFilters,
    menuSearch,
    setMenuSearch,
    menuCategoryId,
    setMenuCategoryId,
    menuPage,
    setMenuPage,
    menuSize,
    setMenuSize,
    groupPage,
    modifierPage,
    comboPage,
    menuItemPage,
    saveGroup,
    saveModifier,
    saveCombo,
    selectDish,
    saveDishGroups,
    reload: load,
  };
};

export default useRestaurantMenuConfiguration;
